"use client"

import { useState } from "react"
import { Link } from "react-router-dom"
import { ArrowUp, ArrowDown, MessageCircle, MoreHorizontal, Flag, Trash2, Edit } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { UserAvatar } from "@/components/users/UserAvatar"
import { CommentForm } from "./CommentForm"
import { useAuth } from "@/context/AuthContext"
import { formatRelativeTime, formatNumber } from "@/lib/utils"
import { voteOnTarget, deleteComment, updateComment } from "@/lib/db"
import { toast } from "sonner"

// Helper to extract date from Firestore Timestamp or ISO string
function getDate(value: unknown): Date {
  if (!value) return new Date()
  if (typeof value === "string") return new Date(value)
  if (value instanceof Date) return value
  if (typeof value === "object" && "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate()
  }
  return new Date()
}

interface CommentCardProps {
  comment: {
    id: string
    authorId: string
    content: string
    upvoteCount?: number
    downvoteCount?: number
    isEdited?: boolean
    createdAt?: unknown
    author?: {
      username?: string
      displayName?: string
      avatarUrl?: string
    }
    replies?: CommentCardProps["comment"][]
  }
  postId: string
  onDelete?: (commentId: string) => void
  depth?: number
}

export function CommentCard({ comment, postId, onDelete, depth = 0 }: CommentCardProps) {
  const { user } = useAuth()
  const [showReplyForm, setShowReplyForm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editContent, setEditContent] = useState(comment.content)
  const [upvoted, setUpvoted] = useState(false)
  const [downvoted, setDownvoted] = useState(false)
  const [voteCount, setVoteCount] = useState((comment.upvoteCount || 0) - (comment.downvoteCount || 0))

  const isAuthor = user?.uid === comment.authorId
  const maxDepth = 3

  const handleUpvote = async () => {
    if (!user) {
      toast.error("Please log in to vote")
      return
    }

    try {
      await voteOnTarget(user.uid, comment.id, "comment", "upvote")

      if (upvoted) {
        setVoteCount(c => c - 1)
        setUpvoted(false)
      } else {
        setVoteCount(c => c + (downvoted ? 2 : 1))
        setUpvoted(true)
        setDownvoted(false)
      }
    } catch (error) {
      console.error("Error voting:", error)
      toast.error("Failed to vote")
    }
  }

  const handleDownvote = async () => {
    if (!user) {
      toast.error("Please log in to vote")
      return
    }

    try {
      await voteOnTarget(user.uid, comment.id, "comment", "downvote")

      if (downvoted) {
        setVoteCount(c => c + 1)
        setDownvoted(false)
      } else {
        setVoteCount(c => c - (upvoted ? 2 : 1))
        setDownvoted(true)
        setUpvoted(false)
      }
    } catch (error) {
      console.error("Error voting:", error)
      toast.error("Failed to vote")
    }
  }

  const handleEdit = async () => {
    if (!editContent.trim()) {
      toast.error("Comment cannot be empty")
      return
    }

    try {
      await updateComment(comment.id, editContent)
      setIsEditing(false)
      toast.success("Comment updated")
    } catch (error) {
      console.error("Error updating comment:", error)
      toast.error("Failed to update comment")
    }
  }

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this comment?")) return

    try {
      await deleteComment(comment.id)
      toast.success("Comment deleted")
      onDelete?.(comment.id)
    } catch (error) {
      console.error("Error deleting comment:", error)
      toast.error("Failed to delete comment")
    }
  }

  return (
    <div className={`${depth > 0 ? "ml-8 border-l-2 border-gray-200 pl-4 dark:border-gray-800" : ""}`}>
      <div className="py-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <Link to={`/user/${comment.author?.username}`}>
              <UserAvatar src={comment.author?.avatarUrl} name={comment.author?.displayName || "User"} size="sm" />
            </Link>
            <div>
              <Link
                to={`/user/${comment.author?.username}`}
                className="text-sm font-medium text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
              >
                {comment.author?.displayName}
              </Link>
              <span className="mx-1 text-gray-400">·</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatRelativeTime(getDate(comment.createdAt))}
                {comment.isEdited && " (edited)"}
              </span>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isAuthor && (
                <>
                  <DropdownMenuItem onClick={() => setIsEditing(true)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-red-600" onClick={handleDelete}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuItem>
                <Flag className="mr-2 h-4 w-4" />
                Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Content */}
        {isEditing ? (
          <div className="mt-2 space-y-2">
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className="w-full min-h-[80px] p-2 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-indigo-600 bg-background text-foreground"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleEdit}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-2 text-gray-700 dark:text-gray-300">
              <p className="whitespace-pre-wrap break-words">{comment.content}</p>
            </div>

            {/* Actions */}
            <div className="mt-3 flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Button variant="ghost" size="icon" className={`h-7 w-7 ${upvoted ? "text-indigo-600" : ""}`} onClick={handleUpvote}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">{formatNumber(voteCount)}</span>
                <Button variant="ghost" size="icon" className={`h-7 w-7 ${downvoted ? "text-red-500" : ""}`} onClick={handleDownvote}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>

              {depth < maxDepth && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowReplyForm(!showReplyForm)}>
                  <MessageCircle className="mr-1 h-3 w-3" />
                  Reply
                </Button>
              )}
            </div>
          </>
        )}

        {/* Reply Form */}
        {showReplyForm && (
          <div className="mt-4">
            <CommentForm
              postId={postId}
              parentId={comment.id}
              onSuccess={() => setShowReplyForm(false)}
              onCancel={() => setShowReplyForm(false)}
              placeholder="Write a reply..."
              autoFocus
            />
          </div>
        )}
      </div>

      {/* Nested Replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="space-y-0">
          {comment.replies.map(reply => (
            <CommentCard key={reply.id} comment={reply} postId={postId} onDelete={onDelete} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}
